import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import popupService from 'services/popup/popup-service';

const HIDE_KEY_PREFIX = 'popup_hide_';

const getToday = () => {
  const date = new Date();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

export function MainPopup() {
  const navigate = useNavigate();
  const [popups, setPopups] = useState([]);

  useEffect(() => {
    async function fetchPopups() {
      try {
        const response = await popupService.list();
        const list = (response && response.success && Array.isArray(response.data) && response.data) || [];
        const today = getToday();
        setPopups(list.filter((popup) => localStorage.getItem(`${HIDE_KEY_PREFIX}${popup.popupNo}`) !== today));
      } catch (e) {
        console.error(e);
      }
    }
    fetchPopups();
  }, []);

  const closePopup = useCallback((popupNo) => {
    setPopups((prev) => prev.filter((popup) => popup.popupNo !== popupNo));
  }, []);

  const hideToday = useCallback(
    (popupNo) => {
      try {
        localStorage.setItem(`${HIDE_KEY_PREFIX}${popupNo}`, getToday());
      } catch (e) {
        console.error(e);
      }
      closePopup(popupNo);
    },
    [closePopup]
  );

  const layerPopups = popups.filter((popup) => popup.popupType === 'layer');
  const topPopups = popups.filter((popup) => popup.popupType === 'top');

  // ESC 키로 가장 위의 레이어 팝업 닫기
  useEffect(() => {
    if (layerPopups.length === 0) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        closePopup(layerPopups[layerPopups.length - 1].popupNo);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [layerPopups, closePopup]);

  // 레이어 팝업이 열려 있는 동안 배경 스크롤 잠금 (Dim과 동일한 동작)
  useEffect(() => {
    if (layerPopups.length === 0) return undefined;
    document.body.style.overflowY = 'hidden';
    return () => {
      document.body.style.overflowY = '';
    };
  }, [layerPopups.length]);

  // 팝업 내부의 내부 링크(/...)는 SPA 라우팅으로 처리
  const handleContentClick = (e) => {
    const anchor = e.target.closest && e.target.closest('a');
    if (!anchor) return;
    const href = anchor.getAttribute('href');
    if (href && href.startsWith('/') && !anchor.getAttribute('target')) {
      e.preventDefault();
      navigate(href);
    }
  };

  if (popups.length === 0) return null;

  return (
    <>
      {topPopups.map((popup) => (
        <TopBar key={popup.popupNo} style={{ backgroundColor: popup.backgroundColor || undefined }}>
          <TopContent onClick={handleContentClick} dangerouslySetInnerHTML={{ __html: popup.content }} />
          <TopActions>
            <ActionButton type="button" onClick={() => hideToday(popup.popupNo)}>
              오늘 하루 보지 않기
            </ActionButton>
            <CloseButton type="button" aria-label="팝업 닫기" onClick={() => closePopup(popup.popupNo)}>
              &times;
            </CloseButton>
          </TopActions>
        </TopBar>
      ))}
      {layerPopups.map((popup) => (
        <Backdrop
          key={popup.popupNo}
          onClick={(e) => {
            if (e.target === e.currentTarget) closePopup(popup.popupNo);
          }}
        >
          <Layer
            role="dialog"
            aria-modal="true"
            aria-label={popup.title}
            style={{
              width: (popup.width && `${popup.width}px`) || undefined,
              backgroundColor: popup.backgroundColor || '#ffffff'
            }}
          >
            <LayerClose type="button" aria-label="팝업 닫기" onClick={() => closePopup(popup.popupNo)}>
              &times;
            </LayerClose>
            <LayerContent
              onClick={handleContentClick}
              style={{ maxHeight: (popup.height && `${popup.height}px`) || undefined }}
              dangerouslySetInnerHTML={{ __html: popup.content }}
            />
            <LayerFooter>
              <ActionButton type="button" onClick={() => hideToday(popup.popupNo)}>
                오늘 하루 보지 않기
              </ActionButton>
              <ActionButton type="button" onClick={() => closePopup(popup.popupNo)}>
                닫기
              </ActionButton>
            </LayerFooter>
          </Layer>
        </Backdrop>
      ))}
    </>
  );
}

const TopBar = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 16px;
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: ${({ theme }) => theme.colors.boxShadow};
`;

const TopContent = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 14px;
  line-height: 1.5;
  word-break: break-word;

  img {
    max-width: 100%;
    height: auto;
    vertical-align: middle;
  }
`;

const TopActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 10;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const Layer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 32px);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.colors.boxShadow};
`;

const LayerContent = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  word-break: break-word;

  img {
    max-width: 100%;
    height: auto;
    vertical-align: middle;
  }
`;

const LayerFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.lighter};
  background-color: ${({ theme }) => theme.colors.white};
`;

const LayerClose = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 50%;
  background-color: rgba(23, 23, 23, 0.08);
  color: ${({ theme }) => theme.colors.strong};
  font-size: 20px;
  line-height: 1;
  cursor: pointer;

  &:hover {
    background-color: rgba(23, 23, 23, 0.16);
  }
`;

const CloseButton = styled.button`
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 50%;
  background-color: transparent;
  color: ${({ theme }) => theme.colors.darker};
  font-size: 20px;
  line-height: 1;
  cursor: pointer;

  &:hover {
    background-color: rgba(23, 23, 23, 0.08);
  }
`;

const ActionButton = styled.button`
  border: 0;
  padding: 6px 8px;
  background-color: transparent;
  color: ${({ theme }) => theme.colors.darker};
  font-size: 13px;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.strong};
    text-decoration: underline;
  }
`;
